import { useCallback } from 'react';
import { PlanProduct, ProductLine, ProductType } from 'queries';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useToast, Button, useBoolModalState } from '@app/core';
import { AddProductLine, EditableProductLine } from 'components';

type GenericProductLine = PlanProduct | ProductLine;

interface Props {
  sessionLinkId?: string;
  productLines?: GenericProductLine[];
  productType: ProductType;
  label?: string;
  duration?: number;
  incentive?: number;
  pricePerParticipant?: boolean;

  addProduct?: (productId: string) => any;
  removeProduct?: (productId: string) => any;
  sendUp?: (productId: string) => any;
  sendDown?: (productId: string) => any;
}

const useStyles = createUseStyles((theme: Theme) => ({
  section: {
    maxWidth: theme.maxContentWidth.largeReduced,
    display: 'flex',
    gap: theme.marginBase * 2,
    alignItems: 'flex-start',
  },
  addContainer: {
    marginTop: theme.marginBase,
  },

  label: {
    ...theme.fonts.label,
    minWidth: theme.marginBase * 15,
    minHeight: theme.height.tiny,
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  addOthers: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

export const PriceEditionLine = ({
  sessionLinkId,
  productLines,
  productType,
  label,
  incentive,
  duration,
  pricePerParticipant,
  addProduct,
  removeProduct,
  sendUp,
  sendDown,
}: Props) => {
  const classes = useStyles();
  const [isAdding, startAdding, stopAdding] = useBoolModalState(false);
  const toast = useToast();

  const onAddProduct = useCallback(
    async (productId: string) => {
      try {
        if (addProduct) {
          await addProduct(productId);
        }
        stopAdding();
      } catch (e) {
        toast.exception(e);
      }
    },
    [addProduct, stopAdding, toast],
  );

  return (
    <div className={classes.section}>
      {!!label && <div className={classes.label}>{label}</div>}
      <div className={classes.content}>
        {productLines?.map((line) => {
          if (!line.product) {
            return null;
          }
          const disabled =
            !!sessionLinkId &&
            (!('sessionLinkId' in line) || !line.sessionLinkId);

          return (
            <EditableProductLine
              key={line.id}
              product={line.product}
              incentive={incentive}
              duration={duration}
              inherited={disabled}
              removeProduct={!disabled ? removeProduct : undefined}
              sendUp={!disabled ? sendUp : undefined}
              sendDown={!disabled ? sendDown : undefined}
            />
          );
        })}
        {(!!isAdding || addProduct) && (
          <div className={classes.addContainer}>
            {!!isAdding && addProduct && (
              <AddProductLine
                productType={productType}
                incentive={incentive}
                duration={duration}
                onCancel={stopAdding}
                addProduct={onAddProduct}
                pricePerParticipant={pricePerParticipant}
              />
            )}
            {!!addProduct && !isAdding && (
              <div className={classes.addOthers}>
                <Button
                  onClick={() => startAdding()}
                  color="green"
                  text="Ajouter"
                  small
                  variant="muted"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
